import { api } from "@/util/api";
import { catApi } from "@/util/logging";

export interface FilialBestand {
   filiale_id: number;
   kurz: string;
   bezeichnung: string;
   menge: number;
   last_zugang: string | null;
   last_abgang: string | null;
}

export interface ConvenienceBestandResponse {
   gtin: string;
   artikel: string;
   filialen: FilialBestand[];
}

export interface SaleRecord {
   quantity: number;
   dateofsale: string;
   receipt_nr: string;
}

export interface ConvenienceAbgangResponse {
   sales: SaleRecord[];
}

export interface DeliveryRecord {
   quantity: number;
   delivery_date: string;
   document_nr: string;
}

export interface ConvenienceZugangResponse {
   deliveries: DeliveryRecord[];
}

export interface BewegungRecord {
   date: string;
   zugang: number;
   abgang: number;
   bestand: number;
}

export interface PaginationLinks {
   self: string;
   prev?: string; // Link to previous time period (older data)
   next?: string; // Link to next time period (newer data), empty if no newer data
}

export interface DatePaginationMeta {
   _links: PaginationLinks;
   totalItems: number;
   hasPrev: boolean; // Whether there is older data available
   hasNext: boolean; // Whether there is newer data available
   dateRangeStart: string; // Start date of current result set
   dateRangeEnd: string; // End date of current result set
}

export interface ConvenienceBewegungResponse {
   bewegungen: BewegungRecord[];
   meta: DatePaginationMeta;
}

export interface BestandCorrectionItemRequest {
   filialeId: number;
   neuerBestand: number;
   bemerkung?: string;
   datum?: string;
}

export interface BestandCorrectionResult {
   filialeId: number;
   alterBestand?: number;
   neuerBestand?: number;
   differenz?: number;
   zeitstempel?: string;
   success: boolean;
   fehler?: string;
}

export interface BestandCorrectionResponse {
   success: boolean;
   ergebnisse: BestandCorrectionResult[];
}

/**
 * ConvenienceBestandModel handles API calls for fetching stock (Bestand) data
 * for Convenience articles across all branches.
 */
export class ConvenienceBestandModel {
   private static bestandCache = new Map<string, ConvenienceBestandResponse>();

   /**
    * Fetch current stock data for a Convenience article by supplier and GTIN.
    * Results are cached to avoid repeated API calls.
    */
   static async getBestand(
      lieferantId: number,
      gtin: string
   ): Promise<ConvenienceBestandResponse> {
      const cacheKey = `${lieferantId}:${gtin}`;

      // Check cache first
      if (this.bestandCache.has(cacheKey)) {
         const cached = this.bestandCache.get(cacheKey);
         if (cached) return cached;
      }

      try {
         const path = `/v1/convenience/${lieferantId}/${gtin}/bestaende`;
         const response = await api.get<ConvenienceBestandResponse>(path);
         const data = response.data as ConvenienceBestandResponse;

         // Cache the result
         this.bestandCache.set(cacheKey, data);

         return data;
      } catch (error) {
         catApi.error(
            `Failed to fetch bestand for GTIN ${gtin} from lieferant ${lieferantId}:`,
            error as Error
         );
         throw error;
      }
   }

   /**
    * Clear the cache (useful for manual refresh)
    */
   static clearCache(): void {
      this.bestandCache.clear();
   }

   /**
    * Clear a specific cache entry
    */
   static clearCacheEntry(lieferantId: number, gtin: string): void {
      const cacheKey = `${lieferantId}:${gtin}`;
      this.bestandCache.delete(cacheKey);
   }

   /**
    * Fetch recent sales (Abgang) data for a Convenience article in a specific branch.
    */
   static async getAbgang(
      lieferantId: number,
      gtin: string,
      filialeId: number,
      limit = 3
   ): Promise<ConvenienceAbgangResponse> {
      try {
         const path = `/v1/convenience/${lieferantId}/${gtin}/bestaende/${filialeId}/abgang?limit=${limit}`;
         const response = await api.get<ConvenienceAbgangResponse>(path);
         return response.data as ConvenienceAbgangResponse;
      } catch (error) {
         catApi.error(
            `Failed to fetch abgang for GTIN ${gtin} in filiale ${filialeId}:`,
            error as Error
         );
         throw error;
      }
   }

   /**
    * Fetch recent deliveries (Zugang) data for a Convenience article in a specific branch.
    */
   static async getZugang(
      lieferantId: number,
      gtin: string,
      filialeId: number,
      limit = 3
   ): Promise<ConvenienceZugangResponse> {
      try {
         const path = `/v1/convenience/${lieferantId}/${gtin}/bestaende/${filialeId}/zugang?limit=${limit}`;
         const response = await api.get<ConvenienceZugangResponse>(path);
         return response.data as ConvenienceZugangResponse;
      } catch (error) {
         catApi.error(
            `Failed to fetch zugang for GTIN ${gtin} in filiale ${filialeId}:`,
            error as Error
         );
         throw error;
      }
   }

   /**
    * Fetch movement history (Bewegungen) data for a Convenience article in a specific branch
    * with cursor-based date pagination for infinite scrolling.
    *
    * Uses HATEOAS links from the backend for navigation. The backend automatically generates
    * prev/next links with adjusted date ranges (default 7-day windows).
    *
    * @param lieferantId Supplier ID
    * @param gtin Article GTIN
    * @param filialeId Branch ID
    * @param von Start date (YYYY-MM-DD), optional - defaults to 7 days before bis
    * @param bis End date (YYYY-MM-DD), optional - defaults to today
    * @returns Response with bewegungen array and HATEOAS _links for prev/next navigation
    */
   static async getBewegungen(
      lieferantId: number,
      gtin: string,
      filialeId: number,
      von?: string,
      bis?: string
   ): Promise<ConvenienceBewegungResponse> {
      try {
         const path = `/v1/convenience/${lieferantId}/${gtin}/bestaende/${filialeId}/bewegungen`;
         const params = new URLSearchParams();

         if (von) params.append("von", von);
         if (bis) params.append("bis", bis);
         const response = await api.get<ConvenienceBewegungResponse>(
            `${path}?${params.toString()}`
         );
         return response.data as ConvenienceBewegungResponse;
      } catch (error) {
         catApi.error(
            `Failed to fetch bewegungen for GTIN ${gtin} in filiale ${filialeId}:`,
            error as Error
         );
         throw error;
      }
   }

   /**
    * Correct (update) the stock level for a specific branch.
    * Only available for users with PERM_SUPERUSER permission.
    *
    * @param lieferantId Supplier ID
    * @param gtin Article GTIN
    * @param filialeId Branch ID
    * @param neuerBestand New stock level
    * @param bemerkung Optional comment/reason for the correction
    * @returns Correction response with old/new values and difference
    */
   static async correctBestand(
      lieferantId: number,
      gtin: string,
      filialeId: number,
      neuerBestand: number,
      bemerkung?: string
   ): Promise<BestandCorrectionResult> {
      try {
         const path = `/v1/convenience/${lieferantId}/${gtin}/bestaende`;
         const entry: BestandCorrectionItemRequest = {
            filialeId,
            neuerBestand,
         };
         if (bemerkung) {
            entry.bemerkung = bemerkung;
         }
         const request = {
            aenderungen: [entry],
         };

         const response = await api.post<BestandCorrectionResponse>(
            path,
            request
         );

         // Clear cache entry after successful update
         this.clearCacheEntry(lieferantId, gtin);

         const data = response.data as BestandCorrectionResponse;
         const result = data?.ergebnisse?.find(
            (r: BestandCorrectionResult) => r.filialeId === filialeId
         );

         if (!result) {
            throw new Error("Kein Ergebnis für die Filiale erhalten");
         }

         return result;
      } catch (error) {
         catApi.error(
            `Failed to correct bestand for GTIN ${gtin} in filiale ${filialeId}:`,
            error as Error
         );
         throw error;
      }
   }
}
